import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { assistantScope, assistantRequest, authCacheScope, listPrompts } from "@rekonas/data-access";
import { Alert, Button } from "@rekonas/ui";
import { useOperationalWorkspace } from "../operationalWorkspace";

export function AiPromptsPage() {
  const access = useOperationalWorkspace(undefined, ["ai.prompts.manage"]);
  const workspace = access.workspace?.slug || "";
  return <main className="operational-main ai-main"><Link to="/recordings">← Recordings</Link>
    <h1>Assistant prompt releases</h1><p>Saving creates an immutable draft. Activation and rollback change the pointer for future requests. Prompt management grants no transcript access.</p>
    <label>Workspace <select aria-label="Prompt workspace" value={workspace} onChange={e => access.selectWorkspace(e.target.value)}><option value="">Select workspace</option>
      {access.workspaces.map(w => <option key={w.id} value={w.slug}>{w.display_name}</option>)}</select></label>
    {!access.isPending && !access.workspaces.length && <Alert>Separate assistant prompt management permission is required.</Alert>}
    {workspace && <PromptEditor key={`${authCacheScope()}:${workspace}`} workspace={workspace} />}
  </main>;
}
function PromptEditor({ workspace }: { workspace: string }) {
  const [version, setVersion] = useState("");
  const [instructions, setInstructions] = useState("");
  const [note, setNote] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);
  const cache = useQueryClient();
  const releases = useQuery({ queryKey: [...assistantScope(workspace), "prompts"], queryFn: () => listPrompts(workspace), retry: false, enabled: !denied, refetchInterval: 5000 });
  useEffect(() => {
    if (releases.isError && !denied) {
      setDenied(true); setVersion(""); setInstructions(""); setNote(""); setMessage("");
      cache.removeQueries({ queryKey: [...assistantScope(workspace), "prompts"] });
    }
  }, [releases.isError, denied, cache, workspace]);
  async function act(operation: () => Promise<unknown>) {
    setBusy(true); setMessage("");
    try { await operation(); setMessage("Saved. Review the active release below."); await releases.refetch(); }
    catch (caught) { setMessage(caught instanceof Error ? caught.message : "Prompt operation failed"); }
    finally { setBusy(false); }
  }
  return <section>
    {denied || releases.isError ? <Alert>Current prompt authorization could not be verified. Protected releases are hidden.</Alert> : <>
      <form onSubmit={e => { e.preventDefault(); void act(() => assistantRequest(`ai-prompts/${encodeURIComponent(workspace)}`, { version, instructions, note })); }}>
        <label>Version <input value={version} maxLength={80} onChange={e => setVersion(e.target.value)} /></label>
        <label>Full master instructions <textarea aria-label="Master instructions" value={instructions} maxLength={12000} rows={12} onChange={e => setInstructions(e.target.value)} /></label>
        <label>Change note <input value={note} maxLength={1000} onChange={e => setNote(e.target.value)} /></label>
        <Button type="submit" disabled={busy || !version.trim() || !instructions.trim() || !note.trim()}>Save immutable draft</Button>
      </form>
      <ul className="operator-run-list">{releases.data?.releases.map(r => <li className="operator-run" key={r.id}>
        <strong>{r.version} {releases.data?.active?.prompt_id===r.id && "· Active"}</strong>
        <p className="ai-identity">{r.sha256} · {r.created} · {r.creator}</p><p>{r.note}</p><details><summary>Exact instructions</summary><pre>{r.instructions}</pre></details>
        <Button variant="secondary" disabled={busy || releases.data?.active?.prompt_id===r.id} onClick={() => void act(() => assistantRequest(`ai-prompts/${encodeURIComponent(workspace)}/activate`, { prompt_id: r.id, revision: releases.data?.active?.revision || 0 }))}>Activate / roll back to this release</Button>
      </li>)}</ul>
    </>}{message && <p role="status">{message}</p>}
  </section>;
}
